import { useState } from 'react';
import { useFitnessStore } from '../../../store/fitnessStore';
import { useCanAccess } from '../../../hooks/useAccess';
import { generateMealPlan } from '../../../services/anthropic';
import { calculateTDEE, calculateMacros } from '../../../utils/calculations';
import PaywallBanner from '../shared/PaywallBanner';

export default function NutritionScreen() {
  const profile = useFitnessStore((s) => s.profile);
  const openModal = useFitnessStore((s) => s.openModal);
  const incrementAIUsage = useFitnessStore((s) => s.incrementAIUsage);
  const canMealPlan = useCanAccess('ai_meal_plans');
  const canMacroTrack = useCanAccess('nutrition_logging');
  const [generating, setGenerating] = useState(false);
  const [goal, setGoal] = useState('muscle_gain');

  const tdee = calculateTDEE({
    weight: profile.weight, height: profile.height, age: profile.age,
    gender: profile.gender, activityLevel: profile.activityLevel,
  });
  const macros = calculateMacros({ tdee, goal });

  async function handleMealPlan() {
    if (!canMealPlan) { openModal('upgrade'); return; }
    setGenerating(true);
    try {
      const plan = await generateMealPlan({ profile, goal, restrictions: [], tdee });
      openModal('plan', {
        planName: '7-Day Meal Plan',
        planDescription: `${macros.calories} kcal · ${goal.replace('_', ' ')}`,
        weeklyStructure: plan.days?.map((d, i) => ({
          day: i + 1,
          dayName: d.day,
          focus: 'Nutrition',
          exercises: d.meals?.map((m) => ({
            name: m.mealName,
            sets: 1,
            reps: m.foods?.join(', '),
            rest: `${m.calories} kcal`,
            intensity: `P:${m.protein}g C:${m.carbs}g F:${m.fat}g`,
            notes: `Prep: ${m.prepTime}`,
          })) || [],
        })) || [],
        progressionNotes: plan.shoppingList?.join(' · '),
      });
      incrementAIUsage('plans');
    } catch {
      alert('Could not generate meal plan. Check your API key.');
    } finally {
      setGenerating(false);
    }
  }

  const macroSection = (
    <div className="fit-card" style={{ marginBottom: 14 }}>
      <div className="fit-section-label">MACRO TRACKING</div>
      <div className="fit-grid-3">
        {[
          { label: 'PROTEIN', value: macros.protein, color: '#E8FF47' },
          { label: 'CARBS', value: macros.carbs, color: '#00D4FF' },
          { label: 'FAT', value: macros.fat, color: '#FF6B35' },
        ].map((m) => (
          <div key={m.label} style={{ background: '#1C1C1F', borderRadius: 10, padding: 10, textAlign: 'center' }}>
            <div style={{ fontFamily: 'Bebas Neue', fontSize: 24, color: m.color }}>{m.value}g</div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', fontWeight: 600 }}>{m.label}</div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ padding: '20px 16px 0' }}>
      <div style={{ fontFamily: 'Bebas Neue', fontSize: 30, marginBottom: 4 }}>FUEL</div>
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 16 }}>Performance nutrition for athletes</div>

      <div className="fit-card" style={{ marginBottom: 14 }}>
        <div className="fit-section-label">YOUR DAILY TARGETS (FREE)</div>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontFamily: 'Bebas Neue', fontSize: 48, color: '#E8FF47', lineHeight: 1 }}>{macros.calories}</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>calories / day (TDEE adjusted)</div>
        </div>
        <div className="fit-grid-3">
          {[
            { label: 'PROTEIN', value: macros.protein, color: '#E8FF47' },
            { label: 'CARBS', value: macros.carbs, color: '#00D4FF' },
            { label: 'FAT', value: macros.fat, color: '#FF6B35' },
          ].map((m) => (
            <div key={m.label} style={{ background: '#1C1C1F', borderRadius: 10, padding: 10, textAlign: 'center' }}>
              <div style={{ fontFamily: 'Bebas Neue', fontSize: 24, color: m.color }}>{m.value}g</div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', fontWeight: 600 }}>{m.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <div className="fit-section-label">GOAL</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            { id: 'weight_loss', label: 'Fat Loss' },
            { id: 'maintenance', label: 'Maintain' },
            { id: 'muscle_gain', label: 'Build Muscle' },
            { id: 'performance', label: 'Performance' },
          ].map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => setGoal(g.id)}
              style={{
                padding: '7px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
                fontFamily: 'DM Sans', fontWeight: 600, fontSize: 12,
                background: goal === g.id ? '#E8FF47' : 'rgba(255,255,255,0.07)',
                color: goal === g.id ? '#0A0A0B' : 'rgba(255,255,255,0.5)',
              }}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {canMacroTrack ? macroSection : (
        <PaywallBanner feature="Macro Tracking" requiredTier="pro">{macroSection}</PaywallBanner>
      )}

      {canMealPlan ? (
        <div style={{ background: 'linear-gradient(135deg,rgba(232,255,71,0.06),rgba(10,10,11,0))', border: '1px solid rgba(232,255,71,0.18)', borderRadius: 16, padding: 16, marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>✨ 7-Day AI Meal Plan</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 14 }}>Full week of performance meals tailored to your macros, schedule, and goals.</div>
          <button type="button" onClick={handleMealPlan} disabled={generating} className="fit-btn-primary" style={{ opacity: generating ? 0.6 : 1 }}>
            {generating ? '⏳ Creating Your Meal Plan...' : 'Generate My Meal Plan'}
          </button>
        </div>
      ) : (
        <PaywallBanner feature="AI Meal Plans" requiredTier="elite">
          <div className="fit-card">
            <div style={{ fontSize: 13, fontWeight: 600 }}>✨ 7-Day AI Meal Plan</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Full week of performance meals</div>
          </div>
        </PaywallBanner>
      )}

      <div className="fit-card">
        <div className="fit-section-label">PERFORMANCE SUPPLEMENTS</div>
        {[
          { emoji: '💊', name: 'Creatine Monohydrate', dose: '5g/day', note: 'Increase strength & power output' },
          { emoji: '⚡', name: 'Pre-Workout', dose: '1 scoop 30min pre', note: 'Focus & intensity on key sessions' },
          { emoji: '🥛', name: 'Whey Protein', dose: '25-40g post workout', note: 'Hit your daily protein target' },
          { emoji: '🐟', name: 'Omega-3', dose: '2-3g EPA/DHA', note: 'Reduce inflammation & improve recovery' },
        ].map((s) => (
          <div key={s.name} style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'flex-start' }}>
            <div style={{ fontSize: 20, flexShrink: 0 }}>{s.emoji}</div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 1 }}>{s.name}</div>
              <div style={{ fontSize: 10, color: '#E8FF47', marginBottom: 1 }}>{s.dose}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{s.note}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
